import axios from "axios";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5001";
const API_BASE = process.env.REACT_APP_API_BASE || "http://127.0.0.1:5001";
const REFERENCE_DATA_ROUTE = "/reference-data/countries-languages";
const REFERENCE_DATA_COLLATOR = new Intl.Collator("en", { sensitivity: "variant" });

export const mlriComparisonAction = async (action, payload) => {
    const route = action ? `/mlri/model-comparison-v1/${action}` : "/mlri/model-comparison-v1";
    const response = await axios.post(`${BASE_URL}${route}`, payload);
    return response.data;
};

let referenceDataRequest = null;

const normalizeReferenceList = (value, label) => {
    if (!Array.isArray(value)) {
        throw new Error(`Invalid ${label} reference data response.`);
    }

    const normalized = [...new Set(value
        .filter(item => typeof item === "string")
        .map(item => item.trim())
        .filter(Boolean))]
        .sort(REFERENCE_DATA_COLLATOR.compare);

    if (normalized.length === 0) {
        throw new Error(`Invalid ${label} reference data response.`);
    }

    return normalized;
};

const fetchCountryLanguageReferenceData = () => {
    if (!referenceDataRequest) {
        referenceDataRequest = axios
            .get(`${BASE_URL}${REFERENCE_DATA_ROUTE}`)
            .then(response => ({
                countries: normalizeReferenceList(response.data?.countries, "country"),
                languages: normalizeReferenceList(response.data?.languages, "language"),
            }))
            .catch(() => {
                referenceDataRequest = null;
                throw new Error("Unable to load country and language reference data.");
            });
    }

    return referenceDataRequest;
};

export const __resetReferenceDataRequestForTests = () => {
    referenceDataRequest = null;
};

// User ID
export const generateUserId = () => {
    return axios.get(`${BASE_URL}/generate-user-id`);
};

export const resolveProlificEntry = async (payload) => {
    const response = await axios.post(`${BASE_URL}/prolific/entry`, payload);
    return response.data;
};

export const requestProlificExit = async (userId, reason) => {
    const response = await axios.post(`${BASE_URL}/prolific/exit`, { userId, reason });
    return response.data;
};

// Consent
export const submitConsent = (userId) => {
    return axios.post(`${BASE_URL}/submit-consent`, {
        userId,
        consentGiven: true,
        timestamp: new Date().toISOString(),
        elapsedSeconds: 0,
    });
};

// Demographics
export const submitDemographics = async (data) => {
    try {
        const response = await axios.post(`${BASE_URL}/submit-demographics`, data);
        return response.data;
    } catch (error) {
        console.error('[API] Error submitting demographics:', error);
        throw error;
    }
};

// Backend-normalized country and language reference data
export const fetchLanguages = async () => {
    const data = await fetchCountryLanguageReferenceData();
    return [...data.languages];
};

export const fetchCountries = async () => {
    const data = await fetchCountryLanguageReferenceData();
    return [...data.countries];
};

// Movie Selection
export const submitMovieSelection = async (dataToSend) => {
    try {
        const response = await axios.post(`${BASE_URL}/submit-movie-selection`, dataToSend);
        return response.data;
    } catch (error) {
        console.error('[API] Failed to submit movie selection:', error);
        throw error;
    }
};

// Movie Search
export const searchMovies = async (queryParam) => {
    try {
        const query = queryParam?.trim();
        if (!query) {
            return { success: false, message: "Query parameter is required" };
        }

        const response = await axios.get(`${BASE_URL}/tmdb/movies`, {
            params: { q: query }
        });

        return response.data;
    } catch (error) {
        console.error('[API] Error searching movies:', error);
        return { success: false, message: "Error searching movies", error: error.message };
    }
};

export const saveMovieSelection = async (payload) => {
    const res = await axios.post(`${BASE_URL}/movie-selection`, payload);
    return res.data;
};


// 🧑‍💼 Person Search
export const searchPerson = async (queryParam) => {
    try {
        const query = queryParam?.trim();
        if (!query) {
            return { success: false, message: "Query parameter is required." };
        }

        const response = await axios.get(`${BASE_URL}/tmdb/person`, {
            params: { q: query }
        });

        return response.data;
    } catch (error) {
        console.error('[API] Error searching person:', error);
        return { success: false, message: "Error searching person", error: error.message };
    }
};


// People Preferences
export const submitPeoplePreferences = async (data) => {
    try {
        const response = await axios.post(`${BASE_URL}/submit-people-preferences`, data);
        return response.data;
    } catch (error) {
        console.error('[API] Error submitting people preferences:', error);
        throw error;
    }
};

export const submitEmptyPeoplePreferences = async (userId) => {
    return submitPeoplePreferences({
        userId,
        people: [], // o el formato que esperes
    });
};


// Genre Preferences
export const submitGenrePreferences = async (data) => {
    const response = await axios.post(`${BASE_URL}/submit-genre-preferences`, data);
    return response.data;
};

// Attribute Preferences
export const saveAttributePreferences = async (data) => {
    const response = await axios.post(`${BASE_URL}/save-attribute-preferences`, data);
    return response.data;
};

// Rating Threshold
export const submitRatingThreshold = async (userId, rating) => {
    try {
        const res = await axios.post(`${BASE_URL}/submit-rating-threshold`, {
            userId,
            rating,
        });
        return res.data.success;
    } catch (error) {
        console.error('[API] Error submitting rating threshold:', error);
        return false;
    }
};

// Runtime Preferences
export const submitRuntimePreferences = async (data) => {
    try {
        const res = await axios.post(`${BASE_URL}/submit-runtime-preferences`, data);
        return res.data.success;
    } catch (error) {
        console.error('[API] Error submitting runtime preferences:', error);
        throw error;
    }
};

// Preference Intro
export const submitPreferenceIntro = async ({ userId, introTimestamp, introElapsed }) => {
    try {
        const response = await axios.post(`${BASE_URL}/submit-preference-intro`, {
            userId,
            introTimestamp,
            introElapsed
        });
        return response.data;
    } catch (error) {
        console.error('[API] Error submitting preference intro:', error);
        throw error;
    }
};

// Year Preferences
export const submitReleaseYearPreferences = async (data) => {
    try {
        const response = await axios.post(`${BASE_URL}/submit-year-preferences`, data);
        return response.data.success;
    } catch (error) {
        console.error('[API] Error submitting release year preferences:', error);
        return false;
    }
};

// Attribute Weights
export const submitAttributeWeights = async (userId, weights, startTime, elapsedSeconds) => {
    try {
        const response = await axios.post(`${BASE_URL}/submit-attribute-weights`, {
            userId,
            importanceWeights: weights,
            weightsTimestamp: new Date(startTime).toISOString(),
            weightsElapsed: elapsedSeconds
        });
        return response.data;
    } catch (err) {
        console.error("Error submitting attribute weights:", err);
        return { success: false };
    }
};

// Recommendation Survey
export const submitRecommendationSurvey = async (data) => {
    try {
        const response = await axios.post(`${BASE_URL}/submit-recommendation-survey`, data);
        return response.data.success;
    } catch (error) {
        console.error("Error submitting recommendation survey:", error);
        return false;
    }
};

// WMT Intro
export const submitWmtIntro = async ({ userId, wmtIntroTimestamp, wmtIntroElapsed }) => {
    try {
        const res = await axios.post(`${BASE_URL}/submit-wmt-intro`, {
            userId,
            wmtIntroTimestamp,
            wmtIntroElapsed
        });
        return res.data;
    } catch (err) {
        console.error('[API] Error submitting WMT intro:', err);
        throw err;
    }
};

// WMT Practice
export const saveWmtPracticeResults = async (payload) => {
    const res = await axios.post(`${BASE_URL}/submit-wmt-practice`, payload);
    return res.data;
};

// WMT Final
export const saveWmtResults = async (payload) => {
    try {
        const response = await axios.post(`${BASE_URL}/submit-wmt`, payload);
        return response.data;
    } catch (err) {
        console.error('[API] Error saving WMT results:', err);
        throw err;
    }
};

export const startPairwiseSession = async ({ userId, studyId = "study0", assetsVersion = "Study0_Assets_v1" }) => {
    const res = await fetch(`${API_BASE}/pairwise/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, studyId, assetsVersion }),
    });
    return res.json();
};

export const getNextPair = async (sessionId) => {
    const res = await fetch(`${API_BASE}/pairwise/next?sessionId=${encodeURIComponent(sessionId)}`);
    return res.json();
};

export const submitPairwiseAnswer = async ({
    sessionId,
    pairId,
    issuedTrialId,
    answerToken,
    rawAction,
    cannotJudgeReason = null,
    reactionTimeMs = null,
}) => {
    const res = await fetch(`${API_BASE}/pairwise/answer`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            sessionId,
            pairId,
            issuedTrialId,
            answerToken,
            rawAction,
            ...(cannotJudgeReason ? { cannotJudgeReason } : {}),
            reactionTimeMs,
        }),
    });
    return res.json();
};

export const finishPairwiseSession = async (sessionId) => {
    const res = await fetch(`${API_BASE}/pairwise/finish`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sessionId }),
    });
    return res.json();
};

export const getPilotStudyState = async (userId) => {
    const res = await fetch(`${API_BASE}/pilot/study-state?userId=${encodeURIComponent(userId)}`);
    return res.json();
};

const commonRequest = async (path, { method = "GET", query, body } = {}) => {
    const suffix = query ? `?${new URLSearchParams(query).toString()}` : "";
    const res = await fetch(`${API_BASE}${path}${suffix}`, {
        method,
        ...(body ? { headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) } : {}),
    });
    const data = await res.json();
    if (!res.ok || data?.ok === false) {
        const error = new Error(data?.message || "The movie task could not be saved.");
        error.status = res.status;
        error.code = data?.code;
        error.data = data;
        throw error;
    }
    return data;
};

export const startCommonBlock = (userId) => commonRequest("/common-block/start", {
    method: "POST", body: { userId },
});

export const getCommonBlockState = ({ userId, commonSessionId }) => commonRequest("/common-block/state", {
    query: { userId, ...(commonSessionId ? { commonSessionId } : {}) },
});

export const parseCommonNextResponse = (data) => {
    if (data?.done) return data;
    if (data?.answerable !== true || !data.issuanceId || !data.answerToken || !data.left || !data.right) {
        throw new Error("Invalid comparison response from the movie task.");
    }
    return { ...data, kind: "comparison" };
};

export const getNextCommonComparison = async ({ userId, commonSessionId }) => parseCommonNextResponse(
    await commonRequest("/common-block/btrt/next", { query: { userId, commonSessionId } })
);

export const submitCommonRecognition = (payload) => commonRequest("/common-block/recognition", {
    method: "POST", body: payload,
});

export const submitCommonBtrtAnswer = (payload) => commonRequest("/common-block/btrt/answer", {
    method: "POST", body: payload,
});

export const getCommonInformedness = async ({ userId, commonSessionId }) => {
    const data = await commonRequest("/common-block/informedness", { query: { userId, commonSessionId } });
    if (!Array.isArray(data.items) || data.items.length !== 7 || !Array.isArray(data.values) || data.values.length !== 3) {
        throw new Error("Invalid post-comparison informedness state.");
    }
    return data;
};

export const submitCommonInformedness = (payload) => commonRequest("/common-block/informedness", {
    method: "POST", body: payload,
});

export const getCommonDivaItems = async ({ userId, commonSessionId }) => {
    const data = await commonRequest("/common-block/diva/items", { query: { userId, commonSessionId } });
    if (!Array.isArray(data.items)) throw new Error("Invalid movie judgment state.");
    return data;
};

export const submitCommonDivaItem = (payload) => commonRequest("/common-block/diva/item", {
    method: "POST", body: payload,
});

export const getCommonDivaRanking = async ({ userId, commonSessionId }) => {
    const data = await commonRequest("/common-block/diva/ranking", { query: { userId, commonSessionId } });
    if (!Array.isArray(data.movies) || data.movies.length !== 7) throw new Error("Invalid ranking state.");
    return data;
};

export const submitCommonDivaRanking = (payload) => commonRequest("/common-block/diva/ranking", {
    method: "POST", body: payload,
});

export const completeCommonBlock = (payload) => commonRequest("/common-block/complete", {
    method: "POST", body: payload,
});

export const acknowledgeExternalTestIntro = async ({ sessionId, userId }) => {
    const res = await fetch(`${API_BASE}/external-test/intro/acknowledge`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sessionId, userId }),
    });
    return res.json();
};

export const getNextExternalTestPair = async ({ sessionId, userId }) => {
    const query = new URLSearchParams({ sessionId, userId });
    const res = await fetch(`${API_BASE}/external-test/next?${query.toString()}`);
    return res.json();
};

export const submitExternalTestAnswer = async ({
    sessionId,
    userId,
    pairId,
    issuedTrialId,
    answerToken,
    rawAction,
    cannotJudgeReason = null,
    reactionTimeMs = null,
    clientResponseId = null,
}) => {
    const res = await fetch(`${API_BASE}/external-test/answer`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            sessionId,
            userId,
            pairId,
            issuedTrialId,
            answerToken,
            rawAction,
            ...(cannotJudgeReason ? { cannotJudgeReason } : {}),
            reactionTimeMs,
            ...(clientResponseId ? { clientResponseId } : {}),
        }),
    });
    return res.json();
};

export const savePairwiseResponses = async (responses) => {
    try {
        const res = await axios.post(`${BASE_URL}/save-pairwise-responses`, { responses });
        return res.data;
    } catch (error) {
        console.error('[API] Error saving pairwise responses:', error);
        throw error;
    }
};

export const saveMovieWeekSelections = async (data) => {
    try {
        const res = await axios.post(`${BASE_URL}/save-movie-week-selections`, data);
        return res.data;
    } catch (error) {
        console.error('[API] Error saving movie week selections:', error);
        throw error;
    }
};

export const getMovieWeekCatalog = async (userId) => {
    try {
        const res = await axios.get(`${BASE_URL}/movie-week/catalog`, {
            params: { userId }
        });
        return res.data;
    } catch (error) {
        console.error('[API] Error loading movie week catalog:', error);
        throw error;
    }
};

export const saveFinalResponse = async (userId) => {
    try {
        const response = await axios.post(`${BASE_URL}/save-response`, { userId });
        return response.data;
    } catch (error) {
        console.error('[API] Error saving final survey response:', error);
        throw error;
    }
};

// --- Final Comparisons Task ---
export const startFinalComparisonSession = async ({ userId }) => {
    const res = await fetch(`${API_BASE}/final-comparisons/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId }),
    });
    return res.json();
};

export const getNextFinalComparison = async (sessionId) => {
    const res = await fetch(`${API_BASE}/final-comparisons/next?sessionId=${encodeURIComponent(sessionId)}`);
    return res.json();
};

export const submitFinalComparisonAnswer = async ({
    sessionId,
    pairId,
    i,
    j,
    choice,
    confidence = null,
    reason = null,
    reactionTimeMs = null,
}) => {
    const res = await fetch(`${API_BASE}/final-comparisons/answer`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sessionId, pairId, i, j, choice, confidence, reason, reactionTimeMs }),
    });
    return res.json();
};

export const finishFinalComparisonSession = async (sessionId) => {
    const res = await fetch(`${API_BASE}/final-comparisons/finish`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sessionId }),
    });
    return res.json();
};

// Post-Pairwise Survey
export const submitPostPairwiseSurvey = async (data) => {
    try {
        const res = await axios.post(`${BASE_URL}/submit-post-pairwise-survey`, data);
        return res.data;
    } catch (error) {
        console.error('[API] Error saving post-pairwise survey:', error);
        throw error;
    }
};

// Post-Study Survey
export const submitPostStudySurvey = async (data) => {
    try {
        const res = await axios.post(`${BASE_URL}/submit-post-study-survey`, data);
        return res.data;
    } catch (error) {
        console.error('[API] Error saving post-study survey:', error);
        throw error;
    }
};

// Trailer Rating
export const submitTrailerRating = async (data) => {
    try {
        const res = await axios.post(`${BASE_URL}/submit-trailer-rating`, data);
        return res.data;
    } catch (error) {
        console.error('[API] Error saving trailer rating:', error);
        throw error;
    }
};
